import * as React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader } from "lucide-react";
const RequestCategoryForm = React.lazy(() =>
  import("./RequestCategoryForm").then((module) => ({ default: module.RequestCategoryForm }))
);
import { Button } from "../ui/button";
import { useCreateRequestCategory } from "@/hooks/useCreateRequestCategory";
import { RequestGalleryContext } from "./RequestGalleryContext";
import { createCategorySchema, type CategoryFormValues } from "@/schemas/request-gallery.schemas";

export type { CategoryFormValues };

interface CreateRequestCategoryFormProps {
  onCancel?: () => void;
  onSuccess?: () => void;
}

export const CreateRequestCategoryForm: React.FC<CreateRequestCategoryFormProps> = ({
  onCancel,
  onSuccess,
}) => {
  const context = React.useContext(RequestGalleryContext);
  const activeCategoryId = context?.activeCategoryId;
  const chatType = context?.type ?? "discuss";
  const handleBack = onCancel ?? context?.handleBack ?? (() => {});

  const methods = useForm({
    resolver: zodResolver(createCategorySchema),
    defaultValues: {
      name: "",
      description: "",
      groups: [],
    },
  });

  const createCategoryMutation = useCreateRequestCategory();

  const onSubmit = async (data: CategoryFormValues) => {
    try {
      await createCategoryMutation.mutateAsync({
        chat_type: chatType,
        name: data.name,
        description: data.description,
        parent_id: activeCategoryId ?? null,
        groups: data.groups,
      });
      if (onSuccess) {
        onSuccess();
      } else {
        handleBack();
      }
    } catch (error) {
      console.error("Error creating category:", error);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          methods.handleSubmit(onSubmit)(e);
        }}
        className="flex flex-col h-full"
      >
        <div className="flex-1 overflow-y-auto p-3">
          <React.Suspense fallback={<Loader className="animate-spin" />}>
            <RequestCategoryForm />
          </React.Suspense>
        </div>
        <div className="px-4 py-3 border-t flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={handleBack}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={createCategoryMutation.isPending}>
            {createCategoryMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
